import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalContent, ModalBasicLayout, ModalFooter } from "@vibe/core/next";
import logger from "../../../lib/logger";
import useLatestColumnValuesResynchronization from "../../../hooks/api/useLatestColumnValues";
import { Loader, Tooltip, Skeleton } from "@vibe/core";
import { getColumnIcon } from "../../../utils/columnIcons";
import {
  formatColumnValueItemResync,
  parseColumnKey,
  hasSlowResyncColumn,
  SLOW_RESYNC_COLUMN_TYPES,
  RESYNC_TOAST_INFO_DURATION_MS,
  RESYNC_TOAST_SLOW_INFO_DURATION_MS,
  RESYNC_TOAST_SUCCESS_DURATION_MS,
  RESYNC_TOAST_ERROR_DURATION_MS,
} from "../../../utils/resync/itemResync/itemResyncUtils";
import useTokenHook from "../../../hooks/context/useTokenHook";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import simbApi from "../../../utils/requests";
import { RESYNC_ALL_COLUMNS_ITEMS } from "../../../data/constants/endpoints";
import { formatDate, formatRelativeTime } from "../../../utils/unixDateFormatter";
import { UserAvatar } from "../../ui/UserAvatar";
import { UserAvatarType } from "../../../types/UserAvatarTypes";
import { RegisterForcePush } from "./useForcePushTracker";

interface ResynchronizationModalProps {
  itemId: number;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  columns: any;
  linkedItems: any;
  handleMutateItemLocations: (columnType?: string, columnTitle?: string, latestValue?: any) => void;
  handleMutateItemDiscrepancy: (
    columnType?: string,
    columnTitle?: string,
    latestValue?: any,
  ) => void;
  handleRevalidateItemView: () => Promise<void>;
  setColumnsResyncing: (columnKeys: string[], isResyncing: boolean) => void;
  // Hands a resync the backend queued through orchestration (202) to the tracker that waits
  // for its socket completions.
  registerForcePush?: RegisterForcePush;
}

const ResynchronizeAllColumnsModal = ({
  itemId,
  showModal,
  setShowModal,
  columns,
  linkedItems,
  handleMutateItemLocations,
  handleMutateItemDiscrepancy,
  handleRevalidateItemView,
  setColumnsResyncing,
  registerForcePush,
}: ResynchronizationModalProps) => {
  const {
    data: latestColumnValues,
    isLoading: isLatestColumnValuesLoading,
    error: latestColumnValuesError,
  } = useLatestColumnValuesResynchronization(itemId, columns);

  const { token } = useTokenHook();
  const [Users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const hasSlowColumn = useMemo(() => hasSlowResyncColumn(Object.keys(columns ?? {})), [columns]);

  const canConfirm =
    !isLatestColumnValuesLoading &&
    !latestColumnValuesError &&
    !!latestColumnValues?.latestValues &&
    Object.keys(latestColumnValues.latestValues).length > 0;

  const handleResynchronizeAllColumns = async () => {
    const valuesToApply = latestColumnValues?.latestValues;
    if (!valuesToApply) return;

    const columnsSnapshot = columns;
    const linkedItemsSnapshot = linkedItems;
    const slowColumnKeys = Object.keys(valuesToApply).filter((key) =>
      SLOW_RESYNC_COLUMN_TYPES.has(parseColumnKey(key).columnType),
    );

    // Optimistically apply the resynced state and dismiss the modal so the
    // user can keep working. The "resync all" call can take a long time when
    // file/doc columns are included, since each attachment is re-uploaded
    // per linked item — there's no reason to hold the user behind a spinner.
    handleMutateItemLocations(undefined, undefined, valuesToApply);
    handleMutateItemDiscrepancy(undefined, undefined, valuesToApply);
    setShowModal(false);

    // Slow columns can't be optimistically updated in-place (per-item file
    // URLs don't exist yet), so flag them as resyncing to swap the green
    // badge for a spinner until the backend finishes.
    setColumnsResyncing(slowColumnKeys, true);

    MondayApiQueryInstance.mondayToastNotification(
      hasSlowColumn
        ? "Resyncing all columns in the background. File and doc columns can take a moment…"
        : "Resyncing all columns…",
      "info",
      hasSlowColumn ? RESYNC_TOAST_SLOW_INFO_DURATION_MS : RESYNC_TOAST_INFO_DURATION_MS,
    );

    // A 202 means the backend queued the resync as orchestration jobs instead of writing
    // synchronously. The tracker owns the spinners and the revalidation from there.
    let queued = false;

    try {
      const response = await simbApi.post(
        RESYNC_ALL_COLUMNS_ITEMS,
        {
          columns: columnsSnapshot,
          latestValues: valuesToApply,
          linkedItems: linkedItemsSnapshot,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 0,
        },
      );

      if (response?.status === 202 && registerForcePush) {
        queued = true;
        const queuedKeys: string[] = (response.data?.queuedColumns ?? []).map(
          (column: { columnKey: string }) => column.columnKey,
        );
        registerForcePush(response.data?.requestId, queuedKeys, hasSlowColumn);

        MondayApiQueryInstance.mondayToastNotification(
          "Resync of all columns queued",
          "info",
          RESYNC_TOAST_INFO_DURATION_MS,
        );
        return;
      }

      MondayApiQueryInstance.mondayToastNotification(
        "All columns resynced",
        "success",
        RESYNC_TOAST_SUCCESS_DURATION_MS,
      );
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error_message ?? error?.message;
      logger.error("Error resynchronizing all columns:", errorMessage);

      MondayApiQueryInstance.mondayToastNotification(
        `Error resyncing all columns: ${errorMessage}.`,
        "error",
        RESYNC_TOAST_ERROR_DURATION_MS,
      );
    } finally {
      if (!queued) {
        // Reconcile the optimistic UI with server truth. On success this confirms
        // the new state and forces file/doc columns to re-fetch from Monday's
        // SDK; on failure this reverts the optimistic discrepancy clear so the
        // resync badges reappear and the user can retry. Keep the in-flight
        // spinners on through revalidation so each icon transitions straight
        // from spinner → final state (check or warning) with no flicker.
        handleRevalidateItemView().finally(() => {
          setColumnsResyncing(slowColumnKeys, false);
        });
      }
    }
  };

  useEffect(() => {
    const fetchUsers = async (latestValues: any) => {
      try {
        setUsersLoading(true);

        const userIds = [
          ...new Set(Object.values(latestValues).map((value: any) => parseInt(value?.user_id))),
        ];

        const users = await MondayApiQueryInstance.getUsersByUserId(userIds);

        setUsers(users);
      } catch (error: any) {
        logger.error("Error fetching users:", error.message);
      } finally {
        setUsersLoading(false);
      }
    };
    if (
      latestColumnValues?.latestValues &&
      Object.keys(latestColumnValues?.latestValues).length > 0
    ) {
      fetchUsers(latestColumnValues?.latestValues);
    }
  }, [latestColumnValues?.latestValues]);

  return (
    <Modal
      id="resync-modal"
      show={showModal}
      onClose={() => setShowModal(false)}
      size="medium"
      className="!overflow-visible"
    >
      {isLatestColumnValuesLoading ? (
        <div className="flex justify-center items-center mx-auto min-h-[331px]">
          <Loader size="medium" color="primary" />
        </div>
      ) : (
        <>
          <ModalBasicLayout className="resync-modal-layout">
            <ModalHeader title="Resynchronize all columns" className="px-8" />
            <ModalContent className="overflow-y-scroll max-h-[32rem]">
              {latestColumnValuesError ? (
                <div className="flex justify-center items-center mx-auto min-h-[331px]">
                  <p className="text-sm">
                    Error. Please try again. Error message: {latestColumnValuesError.message}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex justify-center">
                    <h4>
                      These are the latest values for these columns. Confirm to resynchronize.
                    </h4>
                  </div>
                  <div>
                    <div className="flex flex-col gap-4">
                      {Object.entries(latestColumnValues?.latestValues).map(
                        ([key, value]: [string, any]) => {
                          // Parse columnType and columnTitle properly handling multi-word column types

                          const { columnType, columnTitle } = parseColumnKey(key);

                          return (
                            <div
                              key={key}
                              className="grid grid-cols-[0.5fr_1fr_1fr] gap-4 items-center"
                            >
                              <div className="flex gap-2">
                                <div className="flex justify-center items-center">
                                  {getColumnIcon(columnType)}
                                </div>
                                <div className="overflow-hidden whitespace-nowrap text-ellipsis">
                                  <p className="text-sm">{columnTitle}</p>
                                </div>
                              </div>
                              <Tooltip
                                content={formatColumnValueItemResync(
                                  columnType,
                                  value?.data?.value,
                                  value?.data?.textual_value,
                                )}
                              >
                                <div className="overflow-hidden whitespace-nowrap text-ellipsis">
                                  {formatColumnValueItemResync(
                                    columnType,
                                    value?.data?.value,
                                    value?.data?.textual_value,
                                  )}
                                </div>
                              </Tooltip>
                              <div>
                                <Tooltip
                                  content={`Updated by ${
                                    Users.find(
                                      (user: UserAvatarType) =>
                                        parseInt(user?.id as string) === parseInt(value?.user_id),
                                    )?.name || ""
                                  } on ${value?.created_at ? formatDate(value?.created_at) : ""}`}
                                >
                                  <div className="flex gap-3 items-center cursor-pointer">
                                    {usersLoading ? (
                                      <Skeleton type="circle" width={30} height={30} />
                                    ) : (
                                      <UserAvatar
                                        name={
                                          Users.find(
                                            (user: UserAvatarType) =>
                                              parseInt(user?.id as string) ===
                                              parseInt(value?.user_id),
                                          )?.name || ""
                                        }
                                        photoUrl={
                                          Users.find(
                                            (user: UserAvatarType) =>
                                              parseInt(user?.id as string) ===
                                              parseInt(value?.user_id),
                                          )?.photo_thumb
                                        }
                                        size="md"
                                        className="-mx-1 border-2 border-[#f5f6f8] cursor-pointer"
                                      />
                                    )}
                                    <span>
                                      {value?.created_at
                                        ? formatRelativeTime(value?.created_at)
                                        : ""}
                                    </span>
                                  </div>
                                </Tooltip>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                </div>
              )}
            </ModalContent>
          </ModalBasicLayout>
          <ModalFooter
            primaryButton={{
              onClick: handleResynchronizeAllColumns,
              text: "Confirm",
              disabled: !canConfirm,
            }}
            secondaryButton={{
              onClick: () => setShowModal(false),
              text: "Cancel",
            }}
            className="rounded-b-2xl"
          />
        </>
      )}
    </Modal>
  );
};

export default ResynchronizeAllColumnsModal;
